import type { SpApiConnection, SpApiRegion } from "@adpilot/shared";
import type { AccountConnection, AccountShop } from "./amazon-api";

export interface AmazonAuthorizationRow {
  adsConnection: AccountConnection;
  adsStatus: "attention" | "connected";
  region: SpApiRegion;
  shop: AccountShop;
  spApiConnection: SpApiConnection | null;
  spApiStatus: "attention" | "connected" | "notConnected";
}

export function deriveAmazonAuthorizationRows(
  adsConnections: readonly AccountConnection[],
  spApiConnections: readonly SpApiConnection[],
): AmazonAuthorizationRow[] {
  const activeSpApiConnections = spApiConnections.filter(
    (connection) => connection.status !== "revoked",
  );

  return adsConnections.flatMap((adsConnection) =>
    adsConnection.shops.map((shop) => {
      const spApiConnection =
        activeSpApiConnections.find((connection) =>
          connection.linkedShops.some((linkedShop) => linkedShop.shopId === shop.shopId),
        ) ?? null;
      const spApiStatus = !spApiConnection
        ? "notConnected"
        : spApiConnection.health === "connected"
          ? "connected"
          : "attention";

      return {
        adsConnection,
        adsStatus:
          adsConnection.status === "connected" && shop.status === "connected"
            ? "connected"
            : "attention",
        region: spApiConnection?.region ?? regionForMarketplace(shop.marketplaceName),
        shop,
        spApiConnection,
        spApiStatus,
      };
    }),
  );
}

function regionForMarketplace(marketplaceName: string): SpApiRegion {
  const code = marketplaceCode(marketplaceName);
  if (["UK", "DE", "FR", "IT", "ES", "NL", "SE", "PL", "BE"].includes(code)) return "eu";
  if (["JP", "AU", "SG", "IN"].includes(code)) return "fe";
  return "na";
}

function marketplaceCode(marketplaceName: string): string {
  const normalized = marketplaceName.trim();
  const upper = normalized.toUpperCase();
  if (upper === "GB") return "UK";
  if (/^[A-Z]{2}$/.test(upper)) return upper;

  const name = normalized.toLocaleLowerCase();
  const labels: Array<[string, string]> = [
    ["united states", "US"],
    ["united kingdom", "UK"],
    ["germany", "DE"],
    ["france", "FR"],
    ["italy", "IT"],
    ["spain", "ES"],
    ["netherlands", "NL"],
    ["sweden", "SE"],
    ["poland", "PL"],
    ["belgium", "BE"],
    ["canada", "CA"],
    ["mexico", "MX"],
    ["brazil", "BR"],
    ["japan", "JP"],
    ["australia", "AU"],
    ["singapore", "SG"],
    ["india", "IN"],
  ];
  const domainLabels: Array<[string, string]> = [
    [".co.uk", "UK"],
    [".com.mx", "MX"],
    [".com.br", "BR"],
    [".co.jp", "JP"],
    [".com.au", "AU"],
    [".com.sg", "SG"],
    [".com", "US"],
    [".ca", "CA"],
    [".de", "DE"],
    [".fr", "FR"],
    [".it", "IT"],
    [".es", "ES"],
    [".nl", "NL"],
    [".se", "SE"],
    [".pl", "PL"],
    [".com.be", "BE"],
    [".in", "IN"],
  ];
  return (
    labels.find(([label]) => name.includes(label))?.[1] ??
    domainLabels.find(([domain]) => name.includes(domain))?.[1] ??
    "US"
  );
}

export function marketplaceDisplayName(marketplaceName: string, language: string): string {
  const code = marketplaceCode(marketplaceName);
  const regionCode = code === "UK" ? "GB" : code;

  return (
    new Intl.DisplayNames([language], { type: "region" }).of(regionCode) ?? marketplaceName.trim()
  );
}
